import type {
  AccessibleChatbotDTO,
  Chatbot,
  ChatbotConversation,
  ChatbotMembership,
  ChatbotMessage,
  ChatbotTrainingExample,
} from '@liks/backend-svc-api/esm/models/index';
import type {
  AccessibleChatbotWithIdentity,
  ChatbotConversationWithIdentity,
  ChatbotMemberRole,
  ChatbotMembershipWithIdentity,
  ChatbotMessageWithIdentity,
  ChatbotTrainingExampleWithIdentity,
  ChatbotWithIdentity,
} from '../types/chatbot';

export function hasMembershipIdentity(
  member: ChatbotMembership,
): member is ChatbotMembershipWithIdentity {
  return (
    typeof member.id === 'string' &&
    member.id.length > 0 &&
    typeof member.userId === 'string' &&
    member.userId.length > 0
  );
}

export function hasChatbotIdentity(
  chatbot: Chatbot,
): chatbot is ChatbotWithIdentity {
  return (
    typeof chatbot.id === 'string' &&
    chatbot.id.length > 0 &&
    typeof chatbot.name === 'string' &&
    chatbot.name.trim().length > 0
  );
}

const CHATBOT_MEMBER_ROLES: readonly ChatbotMemberRole[] = [
  'ADMIN',
  'EDITOR',
  'VIEWER',
];

export function isChatbotMemberRole(
  value: unknown,
): value is ChatbotMemberRole {
  return (
    typeof value === 'string' &&
    CHATBOT_MEMBER_ROLES.includes(value as ChatbotMemberRole)
  );
}

export function isAccessibleChatbotWithIdentity(
  entry: AccessibleChatbotDTO,
): entry is AccessibleChatbotWithIdentity {
  return (
    entry.chatbot != null &&
    hasChatbotIdentity(entry.chatbot) &&
    isChatbotMemberRole(entry.role)
  );
}

export function hasConversationIdentity(
  conversation: ChatbotConversation,
): conversation is ChatbotConversationWithIdentity {
  return typeof conversation.id === 'string' && conversation.id.length > 0;
}

export function hasMessageIdentity(
  message: ChatbotMessage,
): message is ChatbotMessageWithIdentity {
  return typeof message.id === 'string' && message.id.length > 0;
}

export function hasTrainingExampleIdentity(
  example: ChatbotTrainingExample,
): example is ChatbotTrainingExampleWithIdentity {
  return (
    typeof example.id === 'string' &&
    example.id.length > 0 &&
    typeof example.question === 'string' &&
    typeof example.answer === 'string'
  );
}
